import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'

const HelpingTheEnvironmentPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pale-aqua via-ice-aqua to-white font-sans">
      {/* Hero Section */}
      <section className="relative pt-12 pb-16 px-4 md:px-8 max-w-6xl mx-auto text-center">
        <div className="flex flex-col items-center gap-6">
          {/* Fun header with emoji */}
          <div className="text-6xl md:text-8xl animate-bounce">🌊</div>
          
          <h1 className="text-3xl md:text-5xl font-bold text-dark-navy leading-tight">
            Thea's Mission: <span className="text-medium-teal">Saving Our Planet!</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-dark-navy/80 max-w-2xl">
            When you play Thea's Games, you're helping to clean up the air and protect our beautiful oceans! 🐠💙
          </p>
          
          <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border-2 border-light-teal">
            <img 
              src="/assets/carbon/stripe-climate-logo.svg" 
              alt="Stripe Climate" 
              className="w-6 h-6"
            />
            <span className="text-medium-purple font-semibold">Proud Climate Heroes!</span>
          </div>
        </div>
      </section>

      {/* Ocean Scene Image */}
      <div className="relative w-full overflow-hidden rounded-3xl mx-auto max-w-6xl px-4 mb-12">
        <div 
          className="w-full h-64 md:h-96 bg-cover bg-center rounded-3xl shadow-xl border-4 border-white"
          style={{ 
            backgroundImage: 'url("/assets/carbon/background.jpg")',
          }}
        />
        <div className="absolute inset-4 flex items-center justify-center">
          <div className="bg-dark-navy/60 backdrop-blur-sm text-white px-8 py-4 rounded-2xl text-center">
            <p className="text-lg md:text-2xl font-bold">Together, we're making a difference! 🌍✨</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 pb-16">
        {/* What We Do - Kid Friendly */}
        <section className="max-w-4xl mx-auto px-4 mb-16 text-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border-2 border-pale-aqua">
            <div className="text-5xl mb-4">🧜‍♀️💨</div>
            <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-4">
              How Does It Work?
            </h2>
            <p className="text-lg md:text-xl text-dark-navy/80 leading-relaxed">
              Every time your family plays <span className="text-medium-teal font-bold">Thea's Games</span>, 
              a little bit of money goes to amazing scientists and inventors who are building 
              cool machines that <span className="text-bright-coral font-bold">suck bad stuff out of the air!</span> 
              This helps keep our oceans healthy and our planet happy! 🐋🌈
            </p>
          </div>
        </section>

        {/* Why We Help */}
        <section className="bg-gradient-to-r from-medium-teal/20 via-light-teal/30 to-medium-purple/20 py-12 px-4 mb-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="text-8xl md:text-9xl">🐢</div>
              <div className="text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-4">
                  Why Does Thea Care? 💙
                </h2>
                <p className="text-lg text-dark-navy/80 leading-relaxed">
                  Thea the mermaid loves her ocean home! She knows that keeping the air clean 
                  helps keep the ocean clean too. When there's too much bad air (called CO₂), 
                  the ocean gets too warm and too sour, which makes her fish friends sad. 
                  That's why we're all working together to help! 🐠🦀🐙
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Stats - Fun Version */}
        <section className="max-w-5xl mx-auto px-4 mb-16">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border-2 border-pale-aqua">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-2">
                Our Super Team! 🦸‍♀️🦸‍♂️
              </h2>
              <p className="text-lg text-dark-navy/70">
                Thea's Games is part of a huge team called <span className="text-medium-purple font-bold">Stripe Climate</span>
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row justify-center gap-8 md:gap-16">
              <div className="text-center bg-light-teal/20 rounded-2xl p-6">
                <div className="text-4xl md:text-5xl font-bold text-medium-teal mb-2">25,000+</div>
                <div className="text-dark-navy/70 font-medium">Companies Helping! 🏢</div>
              </div>
              <div className="text-center bg-medium-purple/10 rounded-2xl p-6">
                <div className="text-4xl md:text-5xl font-bold text-medium-purple mb-2">39</div>
                <div className="text-dark-navy/70 font-medium">Countries Around the World! 🌍</div>
              </div>
            </div>
            
            <p className="text-center text-dark-navy/70 mt-6 max-w-2xl mx-auto">
              One company can't fix everything alone, but when we all work together, 
              we can do amazing things! It's just like when Thea teams up with her ocean friends! 🤝
            </p>
          </div>
        </section>

        {/* Cool Projects Section */}
        <section className="max-w-5xl mx-auto px-4 mb-16">
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">🔬🚀</div>
            <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-2">
              Super Cool Inventions!
            </h2>
            <p className="text-lg text-dark-navy/70 max-w-2xl mx-auto">
              Check out these amazing machines that are cleaning up the air! 
              They're like giant vacuum cleaners for the sky! 🌤️
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Project 1: Climeworks */}
            <article className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg border-2 border-light-teal hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <video className="w-full h-full object-cover" autoPlay muted loop playsInline>
                  <source src="/assets/carbon/climeworks.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🏔️</span>
                  <img src="/assets/carbon/climeworks-logo.svg" alt="Climeworks" className="h-6" />
                </div>
                <h3 className="text-xl font-bold text-dark-navy mb-2">Sky Vacuum Machine!</h3>
                <p className="text-dark-navy/70">
                  These giant fans in Iceland suck CO₂ right out of the air and then turn it into rock underground! 
                  It's like magic, but it's real science! ✨🪨
                </p>
                <div className="mt-4 bg-light-teal/20 rounded-full px-4 py-2 inline-block">
                  <span className="text-medium-teal font-semibold text-sm">322 tons of CO₂ removed! 💪</span>
                </div>
              </div>
            </article>

            {/* Project 2: Charm Industrial */}
            <article className="bg-white/90 backdrop-blur-sm rounded-3xl overflow-hidden shadow-lg border-2 border-medium-purple hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <video className="w-full h-full object-cover" autoPlay muted loop playsInline>
                  <source src="/assets/carbon/charm.mp4" type="video/mp4" />
                </video>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🌱</span>
                  <img src="/assets/carbon/charm-logo.svg" alt="Charm Industrial" className="h-6" />
                </div>
                <h3 className="text-xl font-bold text-dark-navy mb-2">Plant Power Storage!</h3>
                <p className="text-dark-navy/70">
                  This team turns old plants into a special oil and pumps it deep underground where it stays forever! 
                  The plants already captured CO₂, so now it's safely stored! 🌿⬇️
                </p>
                <div className="mt-4 bg-medium-purple/10 rounded-full px-4 py-2 inline-block">
                  <span className="text-medium-purple font-semibold text-sm">416 tons of CO₂ stored! 🎉</span>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* Why It Matters - Simple Version */}
        <section className="max-w-5xl mx-auto px-4 mb-16">
          <div className="bg-gradient-to-r from-bright-coral/20 to-sandy-coral/20 rounded-3xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-4">
                  Every Little Bit Helps! 🌟
                </h2>
                <p className="text-lg text-dark-navy/80 mb-4">
                  Right now, these amazing machines are expensive to build. But guess what? 
                  When lots of people help (like YOU playing Thea's Games!), the inventors 
                  can make more machines and make them cheaper!
                </p>
                <p className="text-lg text-dark-navy/80">
                  It's like how video games used to be super rare and expensive, but now 
                  everyone can play them! The same thing will happen with planet-saving machines! 🎮➡️🌍
                </p>
              </div>
              <div className="text-center">
                <div className="text-8xl md:text-9xl">🌱</div>
                <p className="text-medium-teal font-bold text-lg mt-2">Growing Together!</p>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg border-2 border-bright-coral">
            <div className="text-5xl mb-4">🎮💙🌊</div>
            <h2 className="text-2xl md:text-3xl font-bold text-dark-navy mb-4">
              Ready to Be a Hero?
            </h2>
            <p className="text-lg text-dark-navy/80 mb-6">
              Every game you play helps protect Thea's ocean home! 
              Jump in and start your adventure today! 🧜‍♀️✨
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/ocean-dash">
                <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all bg-bright-coral hover:bg-sandy-coral text-white">
                  🧜‍♀️ Play Ocean Dash!
                </Button>
              </Link>
              <Link to="/">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6 rounded-full shadow-md hover:shadow-lg transition-all border-medium-teal text-medium-teal hover:bg-medium-teal hover:text-white">
                  🏠 Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default HelpingTheEnvironmentPage
